import { SignatureComponentType } from 'src/constants';
import { SignaturePageComponent } from 'src/store/signaturePage/types';

import { SIGNATURE_FONT, SIGNATURE_FONT_SIZE } from './canvas_constants';

const MAX_TEXT_TO_IMAGE_CACHE_SIZE = 100;
const textToDataEncodedImageCache = new Map<string, string>();
const TEXT_BASELINE_Y_POSITION = 24;

const SIGNATURE_IMAGE_COMPONENT_TYPES = [
  SignatureComponentType.REQUEST_SIGN,
  SignatureComponentType.REQUEST_INITIAL,
  SignatureComponentType.OWNER_SIGN,
  SignatureComponentType.OWNER_INITIAL,
];

const getCachedTextToImage = (cacheKey: string) => {
  const cachedImage = textToDataEncodedImageCache.get(cacheKey);
  if (cachedImage === undefined) {
    return null;
  }

  // Refresh the insertion order on access so the first map entry is always
  // the least recently used item, not simply the oldest cached item.
  textToDataEncodedImageCache.delete(cacheKey);
  textToDataEncodedImageCache.set(cacheKey, cachedImage);
  return cachedImage;
};

const cacheTextToImage = (cacheKey: string, dataUrl: string) => {
  textToDataEncodedImageCache.set(cacheKey, dataUrl);

  if (textToDataEncodedImageCache.size <= MAX_TEXT_TO_IMAGE_CACHE_SIZE) {
    return;
  }

  const leastRecentlyUsedKey = textToDataEncodedImageCache.keys().next().value;
  if (leastRecentlyUsedKey) {
    textToDataEncodedImageCache.delete(leastRecentlyUsedKey);
  }
};

export function clearCanvasContext(
  canvasElement: HTMLCanvasElement,
  canvasContext: CanvasRenderingContext2D,
) {
  canvasContext.clearRect(0, 0, canvasElement.width, canvasElement.height);
}

export function getCanvasContent(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D | null,
  fontSize: number,
  fontFamily: string,
  text: string,
) {
  if (!context) {
    return null;
  }

  context.font = `${fontSize}px ${fontFamily}`;
  const {
    width,
    fontBoundingBoxAscent = fontSize,
    actualBoundingBoxDescent = 0,
  } = context.measureText(text);
  const canvasWidth = Math.ceil(width);
  const canvasHeight = Math.max(
    1,
    Math.ceil(fontBoundingBoxAscent + actualBoundingBoxDescent),
  );

  if (canvas.width !== canvasWidth) {
    canvas.width = canvasWidth;
  }
  if (canvas.height !== canvasHeight) {
    canvas.height = canvasHeight;
  }

  clearCanvasContext(canvas, context);
  // Setting canvas width/height resets the rendering context state.
  context.font = `${fontSize}px ${fontFamily}`;
  context.fillText(text, 0, TEXT_BASELINE_Y_POSITION);

  return {
    width: canvasWidth,
    height: canvasHeight,
    dataUrl: canvas.toDataURL('image/png'),
  };
}

export function convertTextToDataEncodedImage(
  text: string,
  fontSize: number,
  fontFamily: string,
) {
  const cacheKey = `${fontFamily}:${fontSize}:${text}`;
  const cachedImage = getCachedTextToImage(cacheKey);
  if (cachedImage) {
    return cachedImage;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  const canvasContent = getCanvasContent(
    canvas,
    context,
    fontSize,
    fontFamily,
    text,
  );

  if (!canvasContent) {
    return null;
  }

  const { width, height } = canvasContent;

  const scaledCanvas = document.createElement('canvas');
  const scaledContext = scaledCanvas.getContext('2d');
  if (!scaledContext) {
    return null;
  }

  scaledCanvas.width = width;
  scaledCanvas.height = height;

  const scaledCanvasContent = getCanvasContent(
    scaledCanvas,
    scaledContext,
    fontSize,
    fontFamily,
    text,
  );

  if (!scaledCanvasContent) {
    return null;
  }

  cacheTextToImage(cacheKey, scaledCanvasContent.dataUrl);

  return scaledCanvasContent.dataUrl;
}

// Data URLs captured while canvas readback was blank stay valid cache keys
// after canvas starts working, so a retry would reuse the blank image.
export function clearTextToDataEncodedImageCache() {
  textToDataEncodedImageCache.clear();
}

// The final PDF snapshots the DOM, so the rendered signature <img> elements
// must be replaced in place; React will not re-render them on its own.
export async function refreshSignatureImages(
  components: SignaturePageComponent[],
) {
  clearTextToDataEncodedImageCache();

  await Promise.all(
    components.map(async (component) => {
      if (
        !component.imageTextValue ||
        !SIGNATURE_IMAGE_COMPONENT_TYPES.includes(component.componentType)
      ) {
        return;
      }

      const img = document.getElementById(component.key)?.querySelector('img');
      if (!img) {
        return;
      }

      const dataUrl = convertTextToDataEncodedImage(
        component.imageTextValue,
        SIGNATURE_FONT_SIZE,
        SIGNATURE_FONT,
      );
      if (!dataUrl) {
        return;
      }

      img.src = dataUrl;
      // domToPng would otherwise snapshot the previous pixels.
      await img.decode().catch(() => undefined);
    }),
  );
}
